/**
 * 라벨 카탈로그의 축(namespace) 탭과 결함 값의 쓰임 판정(순수 계산).
 *
 * 카탈로그는 성격이 다른 어휘를 한 표에 담는다:
 * - Dataset 을 좁히는 예약 축(environment·source·task)
 * - 사람이 자유롭게 만드는 라벨
 * - **에피소드 리뷰가 부여하는 닫힌 어휘** 결함 목록(`quality`)
 * 결함 목록은 운영자가 뜻을 다듬는 대상이다. 다른 어휘와 섞여 있으면 찾기 어렵다. 그래서 탭으로 나눈다.
 *
 * 탭은 조회 축이고 다른 화면이 아니다. 그래서 목록 소스의 조건 하나로 들어간다
 * (`LabelTableSource.setAxis`). 탭마다 다른 조회 훅을 두면 같은 자리에 남은 이전 응답이 새 탭의
 * 표에 표시된다(ZERG-485 가 이 경우다).
 */

import type { Label, ReviewOptions } from "@/api";

/** 탭 하나. `all` 은 카탈로그 전체다. */
export type LabelAxis = "all" | "quality";

const AXIS_KEY: Record<Exclude<LabelAxis, "all">, string> = {
  quality: "quality",
};

export const DEFAULT_LABEL_AXIS: LabelAxis = "all";

export const LABEL_AXIS_TABS: readonly { value: LabelAxis; label: string }[] = [
  { value: "all", label: "전체" },
  { value: "quality", label: "결함" },
];

/** URL 값 → 탭. 모르는 값은 기본 탭으로 바꾼다. 오래된 딥링크가 빈 화면을 만들지 않게 한다. */
export function normalizeLabelAxis(raw: string | null): LabelAxis {
  return LABEL_AXIS_TABS.some((tab) => tab.value === raw) ? (raw as LabelAxis) : DEFAULT_LABEL_AXIS;
}

/** 이 라벨이 그 탭에 표시되는지 판정한다. */
export function matchesLabelAxis(label: Label, axis: LabelAxis): boolean {
  return axis === "all" || label.key === AXIS_KEY[axis];
}

/**
 * 표에 표시할 쓰임 문구. 결함 축이 아니거나 계약을 아직 받지 못했으면 null 이다(칸을 비운다).
 * 어휘를 받지 못한 것과 "쓰지 않는 값" 은 다른 사실이라 섞지 않는다.
 *
 * 결함 값은 에피소드 리뷰에서만 고른다. 판정 근거는 `/v1/review-options` 의 에피소드 어휘이고
 * 서버가 SoT 다. 어휘에 없는 값은 `사용 안 함` 이다. 카탈로그에 남은 은퇴 값이라서 숨기지 않고
 * 그대로 표시한다.
 */
export function labelUsageText(label: Label, options: ReviewOptions | null): string | null {
  if (options === null) {
    return null;
  }
  const vocab = label.key === AXIS_KEY.quality ? options.episode_quality_tags : null;
  if (vocab === null) {
    return null;
  }
  return vocab.includes(label.value) ? "에피소드 리뷰" : "사용 안 함";
}
